import { apiClient } from './apiClient';

export type CompetencyAudience = 'Colaborador' | 'Gestor' | 'Executivo';

export const COMPETENCY_AUDIENCE_OPTIONS: { value: CompetencyAudience; label: string }[] = [
  { value: 'Colaborador', label: 'Colaborador' },
  { value: 'Gestor', label: 'Gestor' },
  { value: 'Executivo', label: 'Executivo' },
];

export type PerformanceIndicator = {
  id: string;
  name: string;
  description: string;
  guidanceMarkdown: string;
  audience: CompetencyAudience | string;
  sortOrder: number;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type PerformanceIndicatorListResult = {
  items: PerformanceIndicator[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type PerformanceIndicatorUpsertPayload = {
  name: string;
  description?: string;
  guidanceMarkdown?: string;
  audience: CompetencyAudience | string;
  sortOrder?: number;
  isActive?: boolean;
};

export type Competency = {
  id: string;
  indicatorId: string;
  indicatorName: string;
  name: string;
  description: string;
  guidanceMarkdown: string;
  audience: CompetencyAudience | string;
  sortOrder: number;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type CompetencyListResult = {
  items: Competency[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type CompetencyUpsertPayload = {
  indicatorId: string;
  name: string;
  description?: string;
  guidanceMarkdown?: string;
  sortOrder?: number;
  isActive?: boolean;
};

export type EvaluationCycleIndicator = {
  id: string;
  indicatorId: string;
  indicatorName: string;
  audience: string;
  sortOrder: number;
};

export type CompetencyProficiency =
  | 'Compromete'
  | 'AbaixoDoEsperado'
  | 'DentroDoEsperado'
  | 'AcimaDoEsperado'
  | 'Referencia';

export const COMPETENCY_PROFICIENCY_OPTIONS: { value: CompetencyProficiency; label: string; score: number }[] = [
  { value: 'Compromete', label: 'Compromete', score: 1 },
  { value: 'AbaixoDoEsperado', label: 'Abaixo do esperado', score: 2 },
  { value: 'DentroDoEsperado', label: 'Dentro do esperado', score: 3 },
  { value: 'AcimaDoEsperado', label: 'Acima do esperado', score: 4 },
  { value: 'Referencia', label: 'É referência', score: 5 },
];

export type EvaluationCycleCalendar = {
  id: string;
  cycleId: string;
  name: string;
  periodStartUtc: string;
  periodEndUtc: string;
  evaluationCollectionStartUtc: string;
  evaluationCollectionEndUtc: string;
  feedbackCollectionStartUtc?: string | null;
  feedbackCollectionEndUtc?: string | null;
  sortOrder: number;
};

export type EvaluationCycleCalendarUpsertPayload = {
  name: string;
  periodStartUtc: string;
  periodEndUtc: string;
  evaluationCollectionStartUtc: string;
  evaluationCollectionEndUtc: string;
  feedbackCollectionStartUtc?: string | null;
  feedbackCollectionEndUtc?: string | null;
  sortOrder?: number;
};

export type EvaluationFormType = 'Self' | 'Manager' | 'Peers' | 'Upward';

export const EVALUATION_FORM_TYPE_OPTIONS: { value: EvaluationFormType; label: string }[] = [
  { value: 'Self', label: 'Autoavaliação' },
  { value: 'Manager', label: 'Gestor (90°)' },
  { value: 'Peers', label: 'Pares (180°)' },
  { value: 'Upward', label: 'Colaborador (avalia o gestor)' },
];

export type EvaluationForm = {
  id: string;
  cycleId: string;
  name: string;
  formType: EvaluationFormType | string;
  audience: CompetencyAudience | string;
  sortOrder: number;
  questionCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type EvaluationFormUpsertPayload = {
  name: string;
  formType: EvaluationFormType | string;
  audience: CompetencyAudience | string;
  sortOrder?: number;
};

export type EvaluationFormQuestion = {
  id: string;
  formId: string;
  competencyQuestionId: string;
  prompt: string;
  questionType: string;
  competencyId: string;
  competencyName: string;
  indicatorId: string;
  indicatorName: string;
  audience: string;
  sortOrder: number;
};

export type EvaluationFormAvailableQuestion = {
  competencyQuestionId: string;
  prompt: string;
  questionType: string;
  competencyId: string;
  competencyName: string;
  indicatorId: string;
  indicatorName: string;
  audience: string;
  selected: boolean;
};

export type EvaluationCycle = {
  id: string;
  name: string;
  description: string;
  periodStartUtc: string;
  periodEndUtc: string;
  status: string;
  enableSelfEvaluation: boolean;
  enableManagerEvaluation: boolean;
  enablePeersEvaluation: boolean;
  enableUpwardEvaluation: boolean;
  enableCycleFeedback: boolean;
  minPeerReviewers: number;
  maxPeerReviewers: number;
  calendarCount: number;
  calendars: EvaluationCycleCalendar[];
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type EvaluationCycleListResult = {
  items: EvaluationCycle[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type EvaluationCycleUpsertPayload = {
  name: string;
  description?: string;
  periodStartUtc: string;
  periodEndUtc: string;
  enableSelfEvaluation: boolean;
  enableManagerEvaluation: boolean;
  enablePeersEvaluation: boolean;
  enableUpwardEvaluation: boolean;
  enableCycleFeedback: boolean;
  minPeerReviewers: number;
  maxPeerReviewers: number;
};

export type EvaluationRating = {
  id: string;
  competencyId: string;
  indicatorId: string;
  competencyName: string;
  selfProficiency?: string | null;
  managerProficiency?: string | null;
  selfComment: string;
  managerComment: string;
};

export type EvaluationIndicatorFeedback = {
  id: string;
  indicatorId: string;
  indicatorName: string;
  audience: string;
  selfFeedback: string;
  managerFeedback: string;
};

export type PerformanceEvaluation = {
  id: string;
  cycleId: string;
  calendarId: string;
  calendarName: string;
  subjectUserId: string;
  subjectUserName: string;
  managerUserId: string;
  managerUserName: string;
  status: string;
  selfSubmittedAtUtc?: string | null;
  managerSubmittedAtUtc?: string | null;
  selfOverallComment: string;
  managerOverallComment: string;
  ratings: EvaluationRating[];
  indicatorFeedbacks: EvaluationIndicatorFeedback[];
};

export type PerformanceEvaluationListResult = {
  items: PerformanceEvaluation[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type EvaluationPeerReview = {
  id: string;
  cycleId: string;
  calendarId: string;
  calendarName: string;
  subjectUserId: string;
  subjectUserName: string;
  reviewerUserId: string;
  reviewerUserName: string;
  status: string;
  overallComment: string;
  submittedAtUtc?: string | null;
  ratings: Array<{
    id: string;
    competencyId: string;
    competencyName: string;
    proficiency?: string | null;
    comment: string;
  }>;
};

export type EvaluationPeerReviewListResult = {
  items: EvaluationPeerReview[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type CollaboratorNoteRecipient = {
  recipientUserId: string;
  recipientName: string;
  isWitness: boolean;
  deliveredToInbox: boolean;
  emailSent: boolean;
  hasPersonalCopy: boolean;
};

export type CollaboratorNoteAttachment = {
  id: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  downloadUrl?: string | null;
};

export type CollaboratorNote = {
  id: string;
  type: string;
  authorUserId: string;
  authorName: string;
  title: string;
  message: string;
  cycleId?: string | null;
  deliverToInbox: boolean;
  sendByEmail: boolean;
  createdAtUtc: string;
  recipients: CollaboratorNoteRecipient[];
  attachments: CollaboratorNoteAttachment[];
  viewerIsAuthor: boolean;
  viewerHasPersonalCopy: boolean;
  canCopy: boolean;
  canDelete: boolean;
};

export type CollaboratorNoteListResult = {
  items: CollaboratorNote[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type CollaboratorNoteEligibleUser = {
  id: string;
  name: string;
  email: string;
};

export type CollaboratorNoteProfileMode = 'collaborator' | 'manager' | 'hr';

export type CollaboratorNoteEligibleRecipients = {
  profileMode: CollaboratorNoteProfileMode | string;
  canSelectAll: boolean;
  users: CollaboratorNoteEligibleUser[];
};

export type CollaboratorNoteUpsertPayload = {
  type: string;
  title: string;
  message: string;
  cycleId?: string;
  deliverToInbox: boolean;
  sendByEmail: boolean;
  context?: 'personal' | 'company' | string;
  recipients: { userId: string; isWitness: boolean }[];
  selectAllEligible?: boolean;
};

export async function listPerformanceIndicators(params: {
  activeOnly?: boolean;
  search?: string;
  audience?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<PerformanceIndicatorListResult> {
  const response = await apiClient.get<PerformanceIndicatorListResult>('/v1/people/performance-indicators', {
    params,
  });
  return response.data;
}

export async function createPerformanceIndicator(
  payload: PerformanceIndicatorUpsertPayload,
): Promise<PerformanceIndicator> {
  const response = await apiClient.post<PerformanceIndicator>('/v1/people/performance-indicators', payload);
  return response.data;
}

export async function updatePerformanceIndicator(
  id: string,
  payload: PerformanceIndicatorUpsertPayload,
): Promise<PerformanceIndicator> {
  const response = await apiClient.put<PerformanceIndicator>(`/v1/people/performance-indicators/${id}`, payload);
  return response.data;
}

export async function deletePerformanceIndicator(id: string): Promise<void> {
  await apiClient.delete(`/v1/people/performance-indicators/${id}`);
}

export async function listCompetencies(params: {
  activeOnly?: boolean;
  search?: string;
  audience?: string;
  indicatorId?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<CompetencyListResult> {
  const response = await apiClient.get<CompetencyListResult>('/v1/people/competencies', { params });
  return response.data;
}

export async function createCompetency(payload: CompetencyUpsertPayload): Promise<Competency> {
  const response = await apiClient.post<Competency>('/v1/people/competencies', payload);
  return response.data;
}

export async function updateCompetency(id: string, payload: CompetencyUpsertPayload): Promise<Competency> {
  const response = await apiClient.put<Competency>(`/v1/people/competencies/${id}`, payload);
  return response.data;
}

export async function deleteCompetency(id: string): Promise<void> {
  await apiClient.delete(`/v1/people/competencies/${id}`);
}

export type EvaluationQuestionType = 'Open' | 'Scale';

export const EVALUATION_QUESTION_TYPE_OPTIONS: { value: EvaluationQuestionType; label: string }[] = [
  { value: 'Scale', label: 'Múltipla escolha (régua 1–5)' },
  { value: 'Open', label: 'Aberta (texto)' },
];

export type CompetencyQuestion = {
  id: string;
  competencyId: string;
  competencyName: string;
  indicatorId: string;
  indicatorName: string;
  audience: CompetencyAudience | string;
  prompt: string;
  guidanceMarkdown: string;
  questionType: EvaluationQuestionType | string;
  sortOrder: number;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type CompetencyQuestionListResult = {
  items: CompetencyQuestion[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type CompetencyQuestionUpsertPayload = {
  competencyId: string;
  prompt: string;
  guidanceMarkdown?: string;
  questionType: EvaluationQuestionType | string;
  sortOrder?: number;
  isActive?: boolean;
};

export type EvaluationCycleQuestion = {
  id: string;
  cycleId: string;
  competencyId: string;
  competencyName: string;
  sourceCompetencyQuestionId?: string | null;
  prompt: string;
  guidanceMarkdown: string;
  questionType: EvaluationQuestionType | string;
  sortOrder: number;
  isActive: boolean;
};

export type EvaluationCycleQuestionUpsertPayload = {
  competencyId: string;
  prompt: string;
  guidanceMarkdown?: string;
  questionType: EvaluationQuestionType | string;
  sortOrder?: number;
  isActive?: boolean;
};

export async function listCompetencyQuestions(params: {
  competencyId?: string;
  activeOnly?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<CompetencyQuestionListResult> {
  const response = await apiClient.get<CompetencyQuestionListResult>('/v1/people/competency-questions', {
    params,
  });
  return response.data;
}

export async function createCompetencyQuestion(
  payload: CompetencyQuestionUpsertPayload,
): Promise<CompetencyQuestion> {
  const response = await apiClient.post<CompetencyQuestion>('/v1/people/competency-questions', payload);
  return response.data;
}

export async function updateCompetencyQuestion(
  id: string,
  payload: CompetencyQuestionUpsertPayload,
): Promise<CompetencyQuestion> {
  const response = await apiClient.put<CompetencyQuestion>(`/v1/people/competency-questions/${id}`, payload);
  return response.data;
}

export async function deleteCompetencyQuestion(id: string): Promise<void> {
  await apiClient.delete(`/v1/people/competency-questions/${id}`);
}

export async function listCycleQuestions(
  cycleId: string,
  competencyId?: string,
): Promise<EvaluationCycleQuestion[]> {
  const response = await apiClient.get<EvaluationCycleQuestion[]>(
    `/v1/people/evaluation-cycles/${cycleId}/questions`,
    { params: { competencyId: competencyId || undefined } },
  );
  return response.data;
}

export async function createCycleQuestion(
  cycleId: string,
  payload: EvaluationCycleQuestionUpsertPayload,
): Promise<EvaluationCycleQuestion> {
  const response = await apiClient.post<EvaluationCycleQuestion>(
    `/v1/people/evaluation-cycles/${cycleId}/questions`,
    payload,
  );
  return response.data;
}

export async function updateCycleQuestion(
  cycleId: string,
  id: string,
  payload: EvaluationCycleQuestionUpsertPayload,
): Promise<EvaluationCycleQuestion> {
  const response = await apiClient.put<EvaluationCycleQuestion>(
    `/v1/people/evaluation-cycles/${cycleId}/questions/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteCycleQuestion(cycleId: string, id: string): Promise<void> {
  await apiClient.delete(`/v1/people/evaluation-cycles/${cycleId}/questions/${id}`);
}

export async function importCycleQuestionsFromCompetencies(
  cycleId: string,
): Promise<EvaluationCycleQuestion[]> {
  const response = await apiClient.post<EvaluationCycleQuestion[]>(
    `/v1/people/evaluation-cycles/${cycleId}/questions/import-from-competencies`,
  );
  return response.data;
}

export async function listEvaluationCycles(params: {
  search?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<EvaluationCycleListResult> {
  const response = await apiClient.get<EvaluationCycleListResult>('/v1/people/evaluation-cycles', { params });
  return response.data;
}

export async function getEvaluationCycle(id: string): Promise<EvaluationCycle> {
  const response = await apiClient.get<EvaluationCycle>(`/v1/people/evaluation-cycles/${id}`);
  return response.data;
}

export async function createEvaluationCycle(payload: EvaluationCycleUpsertPayload): Promise<EvaluationCycle> {
  const response = await apiClient.post<EvaluationCycle>('/v1/people/evaluation-cycles', payload);
  return response.data;
}

export async function updateEvaluationCycle(id: string, payload: EvaluationCycleUpsertPayload): Promise<EvaluationCycle> {
  const response = await apiClient.put<EvaluationCycle>(`/v1/people/evaluation-cycles/${id}`, payload);
  return response.data;
}

export async function duplicateEvaluationCycle(id: string): Promise<EvaluationCycle> {
  const response = await apiClient.post<EvaluationCycle>(`/v1/people/evaluation-cycles/${id}/duplicate`);
  return response.data;
}

export async function deleteEvaluationCycle(id: string): Promise<void> {
  await apiClient.delete(`/v1/people/evaluation-cycles/${id}`);
}

export async function openEvaluationCycle(id: string): Promise<EvaluationCycle> {
  const response = await apiClient.post<EvaluationCycle>(`/v1/people/evaluation-cycles/${id}/open`);
  return response.data;
}

export async function closeEvaluationCycle(id: string): Promise<EvaluationCycle> {
  const response = await apiClient.post<EvaluationCycle>(`/v1/people/evaluation-cycles/${id}/close`);
  return response.data;
}

export async function listCycleIndicators(cycleId: string): Promise<EvaluationCycleIndicator[]> {
  const response = await apiClient.get<EvaluationCycleIndicator[]>(
    `/v1/people/evaluation-cycles/${cycleId}/indicators`,
  );
  return response.data;
}

export async function replaceCycleIndicators(
  cycleId: string,
  indicatorIds: string[],
): Promise<EvaluationCycleIndicator[]> {
  const response = await apiClient.put<EvaluationCycleIndicator[]>(
    `/v1/people/evaluation-cycles/${cycleId}/indicators`,
    { indicatorIds },
  );
  return response.data;
}

export async function listCycleCalendars(cycleId: string): Promise<EvaluationCycleCalendar[]> {
  const response = await apiClient.get<EvaluationCycleCalendar[]>(
    `/v1/people/evaluation-cycles/${cycleId}/calendars`,
  );
  return response.data;
}

export async function createCycleCalendar(
  cycleId: string,
  payload: EvaluationCycleCalendarUpsertPayload,
): Promise<EvaluationCycleCalendar> {
  const response = await apiClient.post<EvaluationCycleCalendar>(
    `/v1/people/evaluation-cycles/${cycleId}/calendars`,
    payload,
  );
  return response.data;
}

export async function updateCycleCalendar(
  cycleId: string,
  id: string,
  payload: EvaluationCycleCalendarUpsertPayload,
): Promise<EvaluationCycleCalendar> {
  const response = await apiClient.put<EvaluationCycleCalendar>(
    `/v1/people/evaluation-cycles/${cycleId}/calendars/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteCycleCalendar(cycleId: string, id: string): Promise<void> {
  await apiClient.delete(`/v1/people/evaluation-cycles/${cycleId}/calendars/${id}`);
}

export async function listEvaluationForms(cycleId: string): Promise<EvaluationForm[]> {
  const response = await apiClient.get<EvaluationForm[]>(`/v1/people/evaluation-cycles/${cycleId}/forms`);
  return response.data;
}

export async function getEvaluationForm(cycleId: string, id: string): Promise<EvaluationForm> {
  const response = await apiClient.get<EvaluationForm>(`/v1/people/evaluation-cycles/${cycleId}/forms/${id}`);
  return response.data;
}

export async function createEvaluationForm(
  cycleId: string,
  payload: EvaluationFormUpsertPayload,
): Promise<EvaluationForm> {
  const response = await apiClient.post<EvaluationForm>(
    `/v1/people/evaluation-cycles/${cycleId}/forms`,
    payload,
  );
  return response.data;
}

export async function updateEvaluationForm(
  cycleId: string,
  id: string,
  payload: EvaluationFormUpsertPayload,
): Promise<EvaluationForm> {
  const response = await apiClient.put<EvaluationForm>(
    `/v1/people/evaluation-cycles/${cycleId}/forms/${id}`,
    payload,
  );
  return response.data;
}

export async function duplicateEvaluationForm(cycleId: string, id: string): Promise<EvaluationForm> {
  const response = await apiClient.post<EvaluationForm>(
    `/v1/people/evaluation-cycles/${cycleId}/forms/${id}/duplicate`,
  );
  return response.data;
}

export async function deleteEvaluationForm(cycleId: string, id: string): Promise<void> {
  await apiClient.delete(`/v1/people/evaluation-cycles/${cycleId}/forms/${id}`);
}

export async function listEvaluationFormQuestions(
  cycleId: string,
  formId: string,
): Promise<EvaluationFormQuestion[]> {
  const response = await apiClient.get<EvaluationFormQuestion[]>(
    `/v1/people/evaluation-cycles/${cycleId}/forms/${formId}/questions`,
  );
  return response.data;
}

export async function listEvaluationFormAvailableQuestions(
  cycleId: string,
  formId: string,
): Promise<EvaluationFormAvailableQuestion[]> {
  const response = await apiClient.get<EvaluationFormAvailableQuestion[]>(
    `/v1/people/evaluation-cycles/${cycleId}/forms/${formId}/available-questions`,
  );
  return response.data;
}

export async function replaceEvaluationFormQuestions(
  cycleId: string,
  formId: string,
  competencyQuestionIds: string[],
): Promise<EvaluationFormQuestion[]> {
  const response = await apiClient.put<EvaluationFormQuestion[]>(
    `/v1/people/evaluation-cycles/${cycleId}/forms/${formId}/questions`,
    { competencyQuestionIds },
  );
  return response.data;
}

export async function listPerformanceEvaluations(cycleId: string): Promise<PerformanceEvaluationListResult> {
  const response = await apiClient.get<PerformanceEvaluationListResult>(
    `/v1/people/evaluation-cycles/${cycleId}/evaluations`,
  );
  return response.data;
}

export async function getPerformanceEvaluation(
  cycleId: string,
  evaluationId: string,
): Promise<PerformanceEvaluation> {
  const response = await apiClient.get<PerformanceEvaluation>(
    `/v1/people/evaluation-cycles/${cycleId}/evaluations/${evaluationId}`,
  );
  return response.data;
}

export type PerformanceEvaluationSubmitPayload = {
  overallComment?: string;
  ratings: Array<{
    competencyId: string;
    selfProficiency?: string | null;
    managerProficiency?: string | null;
    selfComment?: string;
    managerComment?: string;
  }>;
  indicatorFeedbacks: Array<{
    indicatorId: string;
    selfFeedback?: string;
    managerFeedback?: string;
  }>;
};

export async function submitSelfEvaluation(
  cycleId: string,
  evaluationId: string,
  payload: PerformanceEvaluationSubmitPayload,
): Promise<PerformanceEvaluation> {
  const response = await apiClient.post<PerformanceEvaluation>(
    `/v1/people/evaluation-cycles/${cycleId}/evaluations/${evaluationId}/self-submit`,
    payload,
  );
  return response.data;
}

export async function submitManagerEvaluation(
  cycleId: string,
  evaluationId: string,
  payload: PerformanceEvaluationSubmitPayload,
): Promise<PerformanceEvaluation> {
  const response = await apiClient.post<PerformanceEvaluation>(
    `/v1/people/evaluation-cycles/${cycleId}/evaluations/${evaluationId}/manager-submit`,
    payload,
  );
  return response.data;
}

export async function listPeerReviews(cycleId: string): Promise<EvaluationPeerReviewListResult> {
  const response = await apiClient.get<EvaluationPeerReviewListResult>(
    `/v1/people/evaluation-cycles/${cycleId}/peer-reviews`,
  );
  return response.data;
}

export type MyPerformanceTask = {
  kind: string;
  cycleId: string;
  cycleName: string;
  calendarId?: string | null;
  calendarName: string;
  evaluationId?: string | null;
  peerReviewId?: string | null;
  subjectUserId?: string | null;
  subjectUserName: string;
  status: string;
  windowEndUtc: string;
  formPath: string;
};

export type MyPerformanceInbox = {
  evaluationWindowActive: boolean;
  feedbackWindowActive: boolean;
  tasks: MyPerformanceTask[];
};

export async function fetchMyPerformanceInbox(): Promise<MyPerformanceInbox> {
  const response = await apiClient.get<MyPerformanceInbox>('/v1/people/my-performance/inbox');
  return response.data;
}

export type MyPerformanceFeedbackTarget = {
  userId: string;
  userName: string;
  evaluationId?: string | null;
};

export async function listMyFeedbackTargets(
  cycleId: string,
  calendarId?: string,
): Promise<MyPerformanceFeedbackTarget[]> {
  const response = await apiClient.get<MyPerformanceFeedbackTarget[]>(
    '/v1/people/my-performance/feedback-targets',
    { params: { cycleId, calendarId: calendarId || undefined } },
  );
  return response.data;
}

export async function createMyCycleFeedback(
  cycleId: string,
  payload: { targetUserId: string; message: string; evaluationId?: string | null },
): Promise<void> {
  await apiClient.post('/v1/people/my-performance/feedback', payload, {
    params: { cycleId },
  });
}

export async function getPeerReview(cycleId: string, peerReviewId: string): Promise<EvaluationPeerReview> {
  const response = await apiClient.get<EvaluationPeerReview>(
    `/v1/people/evaluation-cycles/${cycleId}/peer-reviews/${peerReviewId}`,
  );
  return response.data;
}

export async function submitPeerReview(
  cycleId: string,
  peerReviewId: string,
  payload: {
    overallComment: string;
    ratings: Array<{ competencyId: string; proficiency?: string | null; comment: string }>;
  },
): Promise<EvaluationPeerReview> {
  const response = await apiClient.post<EvaluationPeerReview>(
    `/v1/people/evaluation-cycles/${cycleId}/peer-reviews/${peerReviewId}/submit`,
    payload,
  );
  return response.data;
}

export async function assignPeerReview(
  cycleId: string,
  payload: { calendarId: string; subjectUserId: string; reviewerUserId: string },
): Promise<EvaluationPeerReview> {
  const response = await apiClient.post<EvaluationPeerReview>(
    `/v1/people/evaluation-cycles/${cycleId}/peer-reviews`,
    payload,
  );
  return response.data;
}

export async function listCollaboratorNotes(params: {
  cycleId?: string;
  aboutUserId?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<CollaboratorNoteListResult> {
  const response = await apiClient.get<CollaboratorNoteListResult>('/v1/people/notes', { params });
  return response.data;
}

export async function fetchNoteEligibleRecipients(
  context?: 'personal' | 'company' | string,
): Promise<CollaboratorNoteEligibleRecipients> {
  const response = await apiClient.get<CollaboratorNoteEligibleRecipients>(
    '/v1/people/notes/eligible-recipients',
    { params: { context: context || undefined } },
  );
  return response.data;
}

export async function createCollaboratorNote(
  payload: CollaboratorNoteUpsertPayload,
): Promise<CollaboratorNote> {
  const response = await apiClient.post<CollaboratorNote>('/v1/people/notes', payload);
  return response.data;
}

export async function copyCollaboratorNote(id: string): Promise<CollaboratorNote> {
  const response = await apiClient.post<CollaboratorNote>(`/v1/people/notes/${id}/copy`);
  return response.data;
}

export async function deleteCollaboratorNote(id: string): Promise<void> {
  await apiClient.delete(`/v1/people/notes/${id}`);
}

export async function uploadCollaboratorNoteAttachment(
  noteId: string,
  file: File,
): Promise<CollaboratorNoteAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<CollaboratorNoteAttachment>(
    `/v1/people/notes/${noteId}/attachments`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function fetchCollaboratorNoteAttachmentDownloadUrl(
  noteId: string,
  attachmentId: string,
): Promise<{ url: string; fileName: string }> {
  const response = await apiClient.get<{ url: string; fileName: string }>(
    `/v1/people/notes/${noteId}/attachments/${attachmentId}/download-url`,
  );
  return response.data;
}
